import { CommonModule } from '@angular/common';
import {
  Component,
  ChangeDetectionStrategy,
  Input,
  ViewChild,
  ElementRef,
  OnInit,
  OnDestroy,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import pdfjsLib from 'src/assets/pdfjs-config';
import { BehaviorSubject, map } from 'rxjs';
import { TruncateStringPipe } from 'src/app/standalones/pipes/truncate-string.pipe';
import { RenderTask } from 'pdfjs-dist';
import { captureException } from '@sentry/angular';
import { TranslateModule } from '@ngx-translate/core';
import { HiddenPageComponent } from '../hidden-page/hidden-page.component';
import { ChromeCanvasBugWorkaroundDirective } from '../../../directives/chrom-canvas-bug-workaround.directive';

@Component({
  selector: 'app-pdf-page[page][viewport]',
  templateUrl: './pdf-page.component.html',
  styleUrls: ['./pdf-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    TruncateStringPipe,
    HiddenPageComponent,
    ChromeCanvasBugWorkaroundDirective,
    TranslateModule,
  ],
})
export class PdfPageComponent implements OnInit, OnChanges, OnDestroy {
  @Input() page?: pdfjsLib.PDFPageProxy;
  @Input() viewport?: pdfjsLib.PageViewport;
  @Input() pdfName = '';
  @Input() scaleFactor = 1;
  @Input() height?: number;
  @Input() width?: number;
  @Input() hidePage = false;
  @Input() totalPage = 1;
  @Output() pageRendered = new EventEmitter<void>();

  @ViewChild('canvas') canvasElm?: ElementRef<HTMLCanvasElement>;
  renderCanvas = new BehaviorSubject(true);
  hideCanvas$ = this.renderCanvas.asObservable().pipe(map((value) => !value));
  private observer?: IntersectionObserver;

  private latestRenderingTask?: RenderTask;

  constructor(private elementRef: ElementRef) {}

  ngOnInit(): void {
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.renderCanvas.next(true);
          this.renderPdfPageToCanvas();
        } else {
          if (this.renderCanvas.getValue()) {
            this.renderCanvas.next(false);
            this.cleanupCanvas();
          }
        }
      });
    });
    this.observer.observe(this.elementRef.nativeElement);
  }

  ngOnChanges(changes: SimpleChanges): void {
    const pageBecameVisible = !changes?.hidePage?.currentValue && changes?.hidePage?.previousValue;
    const pageChanged = changes?.page && !changes.page.firstChange;
    const viewportChanged = changes?.viewport && !changes.viewport.firstChange;

    if (pageBecameVisible || pageChanged || viewportChanged) {
      this.renderPdfPageToCanvas();
    }
  }

  async renderPdfPageToCanvas(): Promise<void> {
    if (!this.page || !this.viewport || !this.renderCanvas.getValue() || this.hidePage) {
      return;
    }

    const viewport = this.viewport as pdfjsLib.PageViewport;

    // To prevent multiple rendering tasks that are initiating as a result of the user triggering a new render operation before the previous one finishes twice
    // from using the same canvas, which can cause an exception,
    // We made each rendering task run on a separate off-screen canvas, and when the last rendering task completes, it renders itself on the main canvas,
    // This way only one render operation will happen on the main canvas (the canvas the user will see)

    const offScreenCanvas = document.createElement('canvas');
    offScreenCanvas.height = viewport.height;
    offScreenCanvas.width = viewport.width;
    const offScreenCanvasContext = offScreenCanvas.getContext('2d', { alpha: false });
    if (!offScreenCanvasContext) {
      return;
    }

    this.latestRenderingTask?.cancel();
    this.latestRenderingTask = this.page.render({
      canvasContext: offScreenCanvasContext,
      viewport,
    });

    const currentRenderingTask = this.latestRenderingTask;

    this.latestRenderingTask.promise
      .then(() => {
        // no other renders will override this one
        if (currentRenderingTask === this.latestRenderingTask) {
          const mainCanvas = this.canvasElm?.nativeElement;
          if (!mainCanvas) {
            return;
          }
          mainCanvas.height = viewport.height;
          mainCanvas.width = viewport.width;
          const mainCanvasContext = mainCanvas.getContext('2d');
          if (!mainCanvasContext) {
            return;
          }
          mainCanvasContext.drawImage(offScreenCanvas, 0, 0);
          this.pageRendered.emit();
        }
      })
      .catch((e) => {
        if (e.name !== 'RenderingCancelledException') {
          captureException(e);
        }
      });
  }

  cleanupCanvas(): void {
    this.page?.cleanup(true);
    this.context?.clearRect(0, 0, this.canvas?.height as number, this.canvas?.width as number);
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
    this.page?.cleanup();
  }

  get canvas(): HTMLCanvasElement | undefined {
    return this.canvasElm?.nativeElement as HTMLCanvasElement;
  }

  get context(): CanvasRenderingContext2D | undefined | null {
    return this.canvas?.getContext('2d', { alpha: false });
  }
}
